/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import { Modal, Button, Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { Formik, Field } from 'formik';
import PropTypes from 'prop-types';

import { truncateFileName } from '../../utils';
import { FILE_NAME_MAX_LENGTH, MAX_FILE_SIZE, UUID_REG_EXP } from '../../constants';

import LoaderButton from './LoaderButton';
import FieldWrapper from '../form/wrappers/Field';
import TooltipButton from './TooltipButton';

function DocumentUploadModal({
  show,
  types,
  onHide,
  percentage,
  buttonLabel,
  titleLabel,
  descriptionLabel,
  verifications = [],
  isStatement = false,
  initialValues = { files: [] },
}) {
  const { t } = useTranslation();
  const [isSubmitting, setSubmitting] = useState(false);
  const [providers, setProviders] = useState([]);

  const onSubmit = (values) => {
    setSubmitting(true);
    onHide(values, setSubmitting);
  };

  const validationSchema = yup.object().shape({
    verification_uuid: yup
      .string()
      .trim()
      .matches(UUID_REG_EXP, { message: t('common:form.fields.uuid.error') }),
  });

  return (
    <Formik initialValues={initialValues} onSubmit={onSubmit} validationSchema={validationSchema}>
      {({ handleSubmit, setFieldValue, values, errors }) => {
        useEffect(() => {
          setProviders(
            verifications
              .filter(
                (val) =>
                  val.verification_id === values.verification_uuid && val.provider !== undefined
              )
              .map((val) => val.provider)
          );
        }, [values.verification_uuid]);

        return (
          <Modal
            id="doc-upload-modal"
            centered
            backdrop="static"
            show={show}
            size="lg"
            onHide={() => onHide()}
            aria-labelledby="doc-upload-modal-title"
          >
            <Form noValidate autoComplete="new-password" onSubmit={handleSubmit}>
              <Modal.Header closeButton={Object.keys(percentage).length === 0}>
                <Modal.Title id="doc-upload-modal-title" as="h3">
                  {titleLabel || t('common:upload.modal.title')}
                </Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <p className="text-lg text-info">
                  {descriptionLabel || t('common:upload.modal.description')}
                </p>
                {!isStatement && (
                  <Field
                    required
                    readOnly={Object.keys(percentage).length}
                    className="type"
                    type="select"
                    id="docUploadForm.type"
                    name="document_type"
                    label={t('common:upload.form.fields.type.label')}
                    options={types.map((type) => ({ value: type.name, label: type.label }))}
                    handleChange={(e) => {
                      const newValue = types.find((type) => type.name === e.target.value);
                      if (e.target.value !== 'other') {
                        setFieldValue('document_type', newValue.name);
                        setFieldValue('identity_type', newValue.identity_type);
                      }
                    }}
                    component={FieldWrapper}
                  />
                )}
                {verifications.length !== 0 && (
                  <Field
                    className="verification_uuid mt-3"
                    id="editLimitForm.verification_uuid"
                    name="verification_uuid"
                    type="select"
                    options={verifications.map((v) => ({
                      label: `${v.verification_id} (${v.kyc_level})`,
                      value: v.verification_id,
                    }))}
                    label={
                      <>
                        {t(`common:upload.form.fields.verificationUUID.label`)}
                        <TooltipButton
                          placement="top"
                          variant="link"
                          text={t(`common:upload.form.fields.verificationUUID.tooltip`)}
                          className="ms-2 no-underline p-0"
                        >
                          <i className="sila-icon info text-lg" />
                        </TooltipButton>
                      </>
                    }
                    component={FieldWrapper}
                  />
                )}
                {providers.length !== 0 && (
                  <Field
                    className="provider  mt-3"
                    id="editLimitForm.provider"
                    name="provider"
                    type="select"
                    options={providers.map((p) => ({ label: p, value: p }))}
                    label={
                      <>
                        {t(`common:upload.form.fields.provider_name.label`)}
                        <TooltipButton
                          placement="top"
                          variant="link"
                          text={t(`common:upload.form.fields.provider_name.tooltip`)}
                          className="ms-2 no-underline p-0"
                        >
                          <i className="sila-icon info text-lg" />
                        </TooltipButton>
                      </>
                    }
                    component={FieldWrapper}
                  />
                )}
                <Field
                  required
                  className="files mb-0 mt-3"
                  type="file"
                  id="docUploadForm.files"
                  name="files"
                  label={t('common:upload.form.fields.files.placeholder')}
                  percentage={percentage}
                  options={{
                    onDrop: (acceptedFiles) => {
                      const files = acceptedFiles.map(
                        (file) =>
                          new File([file], truncateFileName(file.name, FILE_NAME_MAX_LENGTH), {
                            type: file.type,
                          })
                      );
                      setFieldValue('files', files);
                      return files;
                    },
                    multiple: false,
                    maxSize: MAX_FILE_SIZE,
                    accept: !isStatement
                      ? 'image/jpeg, image/png, application/pdf'
                      : 'image/png, image/svg+xml',
                  }}
                  note={!isStatement && t('common:upload.form.fields.files.description')}
                  component={FieldWrapper}
                />
              </Modal.Body>
              <Modal.Footer>
                <Button
                  disabled={isSubmitting || Object.keys(percentage).length}
                  variant="outline-light"
                  onClick={() => onHide()}
                >
                  {t('common:buttons.cancel')}
                </Button>
                <LoaderButton
                  loadingLabel={t('common:status.uploading')}
                  disabled={
                    !values.files.length ||
                    Object.keys(percentage).length ||
                    Object.keys(errors).length
                  }
                  loading={isSubmitting}
                  type="submit"
                >
                  {buttonLabel || t('common:upload.buttons.add')}
                </LoaderButton>
              </Modal.Footer>
            </Form>
          </Modal>
        );
      }}
    </Formik>
  );
}

DocumentUploadModal.propTypes = {
  /**
   * The visibility boolean
   */
  show: PropTypes.bool.isRequired,
  /**
   * The document types array
   */
  types: PropTypes.array,
  /**
   * The percentage object to store progress integer for each file
   */
  percentage: PropTypes.object.isRequired,
  /**
   * The hide function to close the modal
   */
  onHide: PropTypes.func.isRequired,
};

export default DocumentUploadModal;
